import React from "react";
import { Select as AntdSelect, Modal } from "antd";
import Button from "@dtci/spellbook/Button";
import Input from "@dtci/spellbook/Input";
import Typography from "@dtci/spellbook/Typography";
import {
  SectionHeader,
  TierFormatCard,
  WindowCard,
  ValidationError,
  EmptyState,
  CardLabel,
  TierRowCell,
} from "../../components/shared";
import "./styles.scss";

/**
 * Unified TiersForm combining tier formats, windows, and tier rows.
 * Uses CSS Grid for proper column alignment.
 */
export default function TiersForm({
  formData,
  mode = "create",
  tierFormats,
  formatOptions,
  handleTierFormatChange,
  handleInputChange,
  handleAddTierFormat,
  handleRemoveTierFormat,
  handleTierRowInputChange,
  handleAddTierRow,
  handleRemoveTierRow,
  validationErrors = {},
  showTierRows = false,
}) {
  const isCreate = mode === "create";

  const getRevShareValue = () => {
    if (isCreate) {
      return formData?.revShareType?.value;
    }
    return formData?.revShareType?.toUpperCase();
  };

  const revShareValue = getRevShareValue();
  const disablePercentages =
    revShareValue &&
    [
      "REV_SHARE",
      "NON_REV_SHARE",
      "VOD_PPV",
      "HOTEL",
      "REV_SHARE_PLUS",
      "REV SHARE",
      "NON-REV SHARE",
      "VOD PPV",
      "REV SHARE +",
    ].includes(revShareValue);

  const getPriceType = () => {
    if (isCreate) {
      return formData?.priceType?.value || "";
    }
    return formData?.priceType?.toUpperCase() || "";
  };

  const priceType = getPriceType();

  const handleRemoveFormatWithConfirm = (idx) => {
    Modal.confirm({
      title: <Typography variant={"T40"}>Remove Tier Format</Typography>,
      content: (
        <div>
          <Typography variant={"T30"}>
            Are you sure you wish to remove this tier format column?
          </Typography>
          <Typography variant={"T20"}>
            All tier information for this tier format column will be lost and
            will have to be reentered.
          </Typography>
        </div>
      ),
      okText: "Yes",
      cancelText: "No",
      onOk: () => handleRemoveTierFormat && handleRemoveTierFormat(idx),
    });
  };

  const handleRemoveRowWithConfirm = (rowData) => {
    Modal.confirm({
      title: <Typography variant={"T40"}>Remove Tier Row</Typography>,
      content: (
        <div>
          <Typography variant={"T30"}>
            Are you sure you wish to remove this tier row?
          </Typography>
          <Typography variant={"T20"}>
            All tier information for this tier row will be lost and will have to
            be reentered.
          </Typography>
        </div>
      ),
      okText: "Yes",
      cancelText: "No",
      onOk: () => handleRemoveTierRow && handleRemoveTierRow(rowData),
    });
  };

  const getFormatValue = (tier) => {
    if (isCreate) {
      return Array.isArray(tier.tierAliasFormat) ? tier.tierAliasFormat : [];
    }
    if (typeof tier.tierAliasFormat === "string") {
      const formatParts = tier.tierAliasFormat.split(",").map((s) => s.trim());
      return formatOptions
        .filter(
          (opt) =>
            formatParts.includes(String(opt.value)) ||
            (opt.listId != null && formatParts.includes(String(opt.listId))) ||
            formatParts.includes(String(opt.info)) ||
            formatParts.includes(String(opt.label)),
        )
        .map((opt) => opt.value);
    }
    return Array.isArray(tier.tierAliasFormat) ? tier.tierAliasFormat : [];
  };

  if (!Array.isArray(formData?.windows) || formData.windows.length === 0) {
    return (
      <div>
        <SectionHeader title="Tier Configuration" />
        <EmptyState
          icon="fa-solid fa-window-restore"
          message="Select a Window Sequence above to configure tiers"
        />
      </div>
    );
  }

  const windows = [...formData.windows].sort(
    (a, b) => (a.window?.windowOrder ?? 0) - (b.window?.windowOrder ?? 0),
  );
  const numTierFormats = tierFormats.length;
  const numWindows = windows.length;

  const columnWidth = 250;
  const actionColumnWidth = 40;
  const gridTemplateColumns = `${actionColumnWidth}px repeat(${numTierFormats + numWindows}, ${columnWidth}px)`;

  const getTierRowsData = () => {
    if (isCreate) {
      return formData?.tierRows || [];
    }
    const tierAliases = formData?.tierAliases || [];
    const allTiers =
      tierAliases.length > 0
        ? tierAliases.flatMap((alias) => alias.tiers || [])
        : [];

    const groupedTiers = {};
    allTiers.forEach((tier) => {
      if (!groupedTiers[tier.id.tierNumber]) {
        groupedTiers[tier.id.tierNumber] = [];
      }
      groupedTiers[tier.id.tierNumber].push(tier);
    });

    return Object.keys(groupedTiers).map((tierNumber) => ({
      tierNumber,
      tiers: groupedTiers[tierNumber],
    }));
  };

  const tierRowsData = getTierRowsData();

  const canAddRow = isCreate
    ? priceType === "LICENSE_FEE" ||
      (priceType === "MIN_DEEMED_PRICE" && tierRowsData.length === 0)
    : priceType === "LICENSE FEE" ||
      (priceType === "MIN DEEMED PRICE" && tierRowsData.length === 0);

  const getTierIdx = (aliasIdx, tierNumber) => {
    const tierAliases = formData?.tierAliases || [];
    const alias = tierAliases[aliasIdx];
    if (!alias?.tiers) return -1;
    return alias.tiers.findIndex(
      (t) => String(t.id.tierNumber) === String(tierNumber),
    );
  };

  return (
    <div>
      <SectionHeader
        title="Tier Configuration"
        action={
          <Button
            iconType="fa-solid fa-plus"
            labelText="add"
            styleType="link"
            onClick={handleAddTierFormat}
          >
            Add Tier Format
          </Button>
        }
      />

      <div className="tier-grid-container">
        <div className="tier-grid" style={{ gridTemplateColumns }}>
          <div />

          {tierFormats.map((tier, idx) => {
            const tierNameError = validationErrors[`tierAliasName_${idx}`];
            const tierFormatError = validationErrors[`tierAliasFormat_${idx}`];

            return (
              <TierFormatCard
                key={`tier-format-${idx}`}
                onRemove={() => handleRemoveFormatWithConfirm(idx)}
              >
                <CardLabel icon="fa-solid fa-tag">
                  Tier Format {idx + 1}
                </CardLabel>
                <div className="tier-field">
                  <Typography
                    variant="T20"
                    bold
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Tier Name*
                  </Typography>
                  <div className="tier-input-wrapper">
                    <Input
                      value={tier.tierAliasName}
                      onChange={(e) =>
                        handleTierFormatChange(
                          idx,
                          "tierAliasName",
                          e.target.value,
                        )
                      }
                      id={`tier-alias-${idx}`}
                      name={`tier-alias-${idx}`}
                      placeholder="Enter tier name"
                    />
                  </div>
                  <ValidationError error={tierNameError} />
                </div>
                <div>
                  <Typography
                    variant="T20"
                    bold
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Format*
                  </Typography>
                  <AntdSelect
                    mode="multiple"
                    allowClear
                    showSearch
                    className={`spellbook-select ${tierFormatError ? "input-error" : ""}`}
                    placeholder="Select format(s)"
                    value={getFormatValue(tier)}
                    onChange={(value) =>
                      handleTierFormatChange(idx, "tierAliasFormat", value)
                    }
                    options={formatOptions}
                  />
                  <ValidationError error={tierFormatError} />
                </div>
              </TierFormatCard>
            );
          })}

          {windows.map((w, idx) => {
            const percentageError =
              validationErrors[`percentage_${w.id?.tapWindowId || idx}`];
            const isRequired = !disablePercentages;

            return (
              <WindowCard key={w.id?.tapWindowId || idx}>
                <CardLabel icon="fa-solid fa-window-maximize">
                  Window {idx + 1}
                </CardLabel>
                <div className="tier-field">
                  <Typography
                    variant="T20"
                    bold
                    style={{ display: "block", marginBottom: 4 }}
                  >
                    {w.window?.windowName}
                  </Typography>
                  <Typography
                    variant="T20"
                    style={{
                      color: "#7d92a2",
                      display: "block",
                      marginBottom: 0,
                    }}
                  >
                    {w.window?.windowDuration} {w.window?.windowDurationUnit}
                  </Typography>
                </div>
                <div>
                  <Typography
                    variant="T20"
                    bold
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Percentage{isRequired ? "*" : ""}
                  </Typography>
                  <div className="tier-input-wrapper">
                    <Input
                      value={w.percentage ?? ""}
                      onChange={(e) =>
                        handleInputChange(
                          `percentage_${w.id?.tapWindowId}`,
                          e.target.value,
                        )
                      }
                      id={`percentage-${w.id?.tapWindowId || idx}`}
                      name={`percentage-${w.id?.tapWindowId || idx}`}
                      placeholder="%"
                      disabled={disablePercentages}
                      type="number"
                    />
                  </div>
                  <ValidationError error={percentageError} />
                </div>
              </WindowCard>
            );
          })}

          {showTierRows &&
            tierRowsData.map((rowData, rowIdx) => (
              <React.Fragment key={`row-${rowIdx}`}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    iconType="fa-solid fa-trash"
                    styleType="link"
                    className="tier-row-delete-btn"
                    onClick={() => {
                      if (isCreate) {
                        handleRemoveRowWithConfirm(rowIdx);
                      } else {
                        handleRemoveRowWithConfirm(rowData.tiers);
                      }
                    }}
                    aria-label="Remove tier row"
                  />
                </div>

                {isCreate
                  ? (rowData.aliasColumns || []).map((alias, aliasIdx) => {
                      const aliasError =
                        validationErrors[`tierRow_${rowIdx}_alias_${aliasIdx}`];
                      return (
                        <TierRowCell
                          key={`tierName_${rowIdx}_${aliasIdx}`}
                          showLabel={rowIdx === 0}
                          label="Tier Name*"
                        >
                          <Input
                            value={alias.tierName}
                            onChange={(e) =>
                              handleTierRowInputChange(
                                rowIdx,
                                "alias",
                                aliasIdx,
                                e.target.value,
                              )
                            }
                            placeholder="Tier Name*"
                            style={{ width: "100%", maxWidth: "100%" }}
                          />
                          <ValidationError error={aliasError} />
                        </TierRowCell>
                      );
                    })
                  : // Edit mode: use tierAliases
                    (formData?.tierAliases || []).map((alias, aliasIdx) => {
                      const tierIdx = getTierIdx(aliasIdx, rowData.tierNumber);
                      const tierForAlias = (alias.tiers || []).find(
                        (t) =>
                          String(t.id.tierNumber) ===
                          String(rowData.tierNumber),
                      );
                      const tierName = tierForAlias
                        ? tierForAlias.tierName
                        : "";
                      const tierNameError =
                        validationErrors[`tier_${aliasIdx}_${tierIdx}_name`];

                      return (
                        <TierRowCell
                          key={`tierName_${rowIdx}_${aliasIdx}`}
                          showLabel={rowIdx === 0}
                          label="Tier Name*"
                        >
                          <Input
                            value={tierName}
                            onChange={(e) =>
                              handleTierRowInputChange(
                                rowData.tierNumber,
                                "tierName",
                                aliasIdx,
                                e.target.value,
                              )
                            }
                            placeholder="Tier Name*"
                            style={{ width: "100%", maxWidth: "100%" }}
                          />
                          <ValidationError error={tierNameError} />
                        </TierRowCell>
                      );
                    })}

                {windows.map((w, colIdx) => {
                  const tapWindowId = w.id?.tapWindowId;

                  if (isCreate) {
                    const priceCol = (rowData.priceColumns || []).find(
                      (col) => String(col.tapWindowId) === String(tapWindowId),
                    );
                    const price = priceCol ? priceCol.price : "";
                    const priceError =
                      validationErrors[`tierRow_${rowIdx}_price_${colIdx}`];

                    return (
                      <TierRowCell
                        key={`price_${rowIdx}_${tapWindowId}`}
                        showLabel={rowIdx === 0}
                        label="Price*"
                      >
                        <Input
                          value={price}
                          onChange={(e) =>
                            handleTierRowInputChange(
                              rowIdx,
                              "price",
                              colIdx,
                              e.target.value,
                            )
                          }
                          placeholder="Price*"
                          style={{ width: "100%", maxWidth: "100%" }}
                        />
                        <ValidationError error={priceError} />
                      </TierRowCell>
                    );
                  }

                  const tierForWindow = rowData.tiers.find(
                    (t) => String(t.id.tapWindowId) === String(tapWindowId),
                  );
                  const price = tierForWindow ? tierForWindow.price : "";

                  let priceError = null;
                  (formData?.tierAliases || []).forEach((alias, aIdx) => {
                    const tIdx = (alias.tiers || []).findIndex(
                      (t) =>
                        String(t.id.tierNumber) ===
                          String(rowData.tierNumber) &&
                        String(t.id.tapWindowId) === String(tapWindowId),
                    );
                    if (
                      tIdx >= 0 &&
                      validationErrors[`tier_${aIdx}_${tIdx}_price`]
                    ) {
                      priceError =
                        validationErrors[`tier_${aIdx}_${tIdx}_price`];
                    }
                  });

                  return (
                    <TierRowCell
                      key={`price_${rowIdx}_${tapWindowId}`}
                      showLabel={rowIdx === 0}
                      label="Price*"
                    >
                      <Input
                        value={price}
                        onChange={(e) =>
                          handleTierRowInputChange(
                            rowData.tierNumber,
                            "price",
                            colIdx,
                            e.target.value,
                          )
                        }
                        placeholder="Price*"
                        style={{ width: "100%", maxWidth: "100%" }}
                      />
                      <ValidationError error={priceError} />
                    </TierRowCell>
                  );
                })}
              </React.Fragment>
            ))}
        </div>
      </div>

      {showTierRows && canAddRow && (
        <div className="add-tier-row-container">
          <Button
            iconType="fa-solid fa-plus"
            labelText="addRow"
            styleType="link"
            onClick={handleAddTierRow}
            className="add-tier-row-btn"
          >
            Add Tier Row
          </Button>
        </div>
      )}
    </div>
  );
}
